import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { GDPR_TEXT_VERSION } from "@/lib/gdpr";
import api from "@/lib/api";

const AuthCtx = createContext(null);
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [licenses, setLicenses] = useState({}); // module_key -> license data
  // Permisiunile efective (profilurile de sistem ale rolurilor + profilurile de acces personalizate) — meniul și paginile
  // se deschid după ele. `profileKeys` = cheile venite din profiluri personalizate (deschid și pagini „de Admin/HR”).
  const [permissions, setPermissions] = useState(null);
  const [profileKeys, setProfileKeys] = useState(new Set());
  const [permsLoaded, setPermsLoaded] = useState(false);   // încercarea de încărcare s-a terminat (reușită sau nu)
  const loadPermissions = async () => {
    try {
      const r = await api.get("/auth/permissions");
      setPermissions(new Set(r.data.keys || [])); setProfileKeys(new Set(r.data.profile_keys || []));
    } catch { setPermissions(null); setProfileKeys(new Set()); }
    setPermsLoaded(true);
  };
  const [modulesCatalog, setModulesCatalog] = useState([]);
  // Instalare de client neactivată: nimic în afară de activarea licenței (vezi backend core/activation_gate.py).
  const [activationRequired, setActivationRequired] = useState(false);
  // Comutatoare per client (backend core/feature_flags.py): funcții specifice unui client, în același cod pentru toți.
  const [features, setFeatures] = useState({ flags: {}, labels: {} });
  const loadFeatures = async () => {
    try { const r = await api.get("/config/features"); setFeatures({ flags: r.data.flags || {}, labels: r.data.labels || {} }); }
    catch { /* fără drept sau neactivat: nicio funcție specială */ }
  };

  const loadActivation = async () => {
    try { const r = await api.get("/config/deployment"); setActivationRequired(!!r.data.activation_required); }
    catch { /* rămâne starea anterioară */ }
  };

  const loadLicensing = async () => {
    // Modulele licențiate: mereu disponibile (fără permisiune); detaliile complete doar dacă rolul are dreptul să le vadă
    let base = {};
    try { base = Object.fromEntries(Object.keys((await api.get("/licenses/enabled")).data || {}).map(k => [k, { module_key: k }])); } catch {}
    setLicenses(base);
    try {
      const [a, m] = await Promise.all([api.get("/licenses/active"), api.get("/licenses/modules")]);
      setLicenses(Object.keys(a.data || {}).length ? a.data : base); setModulesCatalog(m.data || []);
    } catch { /* fără dreptul de a vedea detaliile: rămân modulele de bază */ }
  };

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/auth/me"); setUser(r.data);
      await loadLicensing(); await loadActivation(); await loadFeatures(); await loadPermissions();
    } catch { setUser(false); setPermissions(null); setPermsLoaded(false); setLicenses({}); setActivationRequired(false); setFeatures({ flags: {}, labels: {} }); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  // pagina Licență anunță activarea/sincronizarea — deblocăm interfața imediat după activare
  useEffect(() => {
    // după activare/sincronizare: deblocăm interfața și reîncărcăm comutatoarele
    const onChange = () => { loadActivation(); loadFeatures(); };
    window.addEventListener("license-status-changed", onChange);
    return () => window.removeEventListener("license-status-changed", onChange);
  }, []);

  const login = async (username, password, opts = {}) => {
    // bifa GDPR a paginii de autentificare: serverul salvează dovada acceptării (o dată pe utilizator și pe versiune a textului)
    const r = await api.post("/auth/login", { username, password,
      ...(opts.gdprAccepted ? { gdpr_accepted: true, gdpr_text_version: GDPR_TEXT_VERSION } : {}) });
    // cont cu 2FA: parola singură nu deschide sesiunea; urmează codul (login2fa)
    if (r.data.mfa_required) return r.data;
    return finishLogin(r.data);
  };
  const finishLogin = async (data) => { setUser(data); await loadLicensing(); await loadActivation(); await loadFeatures(); await loadPermissions(); return data; };
  const login2fa = async (mfaToken, code) => finishLogin((await api.post("/auth/login/2fa", { mfa_token: mfaToken, code })).data);
  const resend2fa = async (mfaToken) => (await api.post("/auth/login/2fa/resend", { mfa_token: mfaToken })).data;
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false); setPermissions(null); setPermsLoaded(false); setLicenses({}); setActivationRequired(false); setFeatures({ flags: {}, labels: {} });
  };
  const hasRole = (...roles) => user && user.role_names && roles.some(r => user.role_names.includes(r));
  // can("a","b") = are măcar una dintre permisiuni; până se încarcă lista (sau pentru superadmin) nu restricționează
  const can = (...keys) => keys.length === 0 || hasRole("superadmin") || !permissions || keys.some(k => permissions.has(k));
  // has(...) = strict: false până se încarcă lista de permisiuni (pentru decizii „arată funcția de administrare")
  const has = (...keys) => !!(permissions && keys.some(k => permissions.has(k)));
  const permissionsReady = permissions !== null;
  // allowed(roles, perm): o pagină/un meniu e pentru cine are unul dintre `roles` ȘI măcar o cheie din `perm` (profilul
  // de sistem al rolului poate fi restrâns de Admin), SAU pentru cine primește una dintre chei dintr-un profil de acces
  // personalizat (de ex. un Salariat cu profilul „Operator pontaj”). Fără `roles`: doar cheile.
  const allowed = (roles, perm) => {
    if (hasRole("superadmin")) return true;
    const byKeys = !perm || can(...perm);
    if (!roles) return byKeys;
    return (hasRole(...roles) && byKeys) || !!(perm && perm.some(k => profileKeys.has(k)));
  };
  const hasFeature = (key) => !!features.flags[key];
  const hasModule = (key) => {
    if (hasRole("superadmin")) return true;
    return !!licenses[key];
  };
  return <AuthCtx.Provider value={{ user, login2fa, resend2fa, can, has, allowed, permissionsReady, permsLoaded, activationRequired, features, hasFeature, licenses, modulesCatalog, login, logout, refresh, hasRole, hasModule, reloadLicensing: loadLicensing }}>{children}</AuthCtx.Provider>;
}
export const useAuth = () => useContext(AuthCtx);
